// crewbit-admin/src/components/money/InvoicesSegmentChips.tsx
//
// Pill chips for invoice segment filtering — All / Active / Outstanding /
// Paid / Drafts / Cancelled — with count badges driven by
// useInvoices().segmentCounts. Selected chip = filled accent background.
//
// SPEC: §Money.InvoicesListAdmin §3 (D2). Labels reuse INVOICE_SEGMENTS from
// types/invoiceSegments to stay consistent with mobile parity.

import { palette, spacing, transition, typography } from '../../styles/tokens';
import { INVOICE_SEGMENTS, type InvoiceSegment } from '../../types/invoiceSegments';

interface InvoicesSegmentChipsProps {
  segmentCounts: Record<InvoiceSegment, number>;
  activeSegment: InvoiceSegment;
  onSegmentChange: (segment: InvoiceSegment) => void;
}

const SEGMENT_ORDER: InvoiceSegment[] = ['all', 'active', 'outstanding', 'paid', 'drafts', 'cancelled'];

export function InvoicesSegmentChips({ segmentCounts, activeSegment, onSegmentChange }: InvoicesSegmentChipsProps) {
  const labelByKey = new Map(INVOICE_SEGMENTS.map((s) => [s.key, s.label]));
  return (
    <div
      role="tablist"
      aria-label="Invoice segment filter"
      style={{
        display: 'flex',
        gap: spacing.sm,
        flexWrap: 'wrap',
        marginBottom: spacing.md,
      }}
    >
      {SEGMENT_ORDER.map((key) => {
        const isActive = key === activeSegment;
        const count = segmentCounts[key] ?? 0;
        return (
          <button
            key={key}
            role="tab"
            aria-selected={isActive}
            type="button"
            onClick={() => onSegmentChange(key)}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '6px 12px',
              borderRadius: 999,
              border: `1px solid ${isActive ? palette.accent : palette.g200}`,
              background: isActive ? palette.accent : palette.white,
              color: isActive ? palette.white : palette.g700,
              fontSize: typography.size.caption,
              fontWeight: isActive ? typography.weight.semibold : typography.weight.medium,
              fontFamily: 'inherit',
              cursor: 'pointer',
              transition: `background ${transition.fast}, color ${transition.fast}, border-color ${transition.fast}`,
              whiteSpace: 'nowrap',
            }}
          >
            <span>{labelByKey.get(key) ?? key}</span>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 18,
                height: 18,
                padding: '0 6px',
                borderRadius: 999,
                background: isActive ? 'rgba(255,255,255,0.22)' : palette.g100,
                color: isActive ? palette.white : palette.g600,
                fontSize: 11,
                fontWeight: typography.weight.semibold,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {count}
            </span>
          </button>
        );
      })}
    </div>
  );
}
